import React from 'react';
import { DATE_FORMAT, formatDateTime } from 'helpers/dateParser';
import RankedBarRow from 'modules/Common/ui/RankedBarRow';
import CgBlockLoader from 'modules/Common/ui/CgBlockLoader';
import { ERP_SYSTEM_NAMES } from 'modules/Common/config/erpSystems';
import AdminPageHeader from '../components/AdminPageHeader';
import { AccessDeniedPanel, ErrorPanel, isForbidden } from '../components/AdminStatePanels';
import { useCgTable } from 'modules/Common/hooks/useCgTable';
import ErpRequestsTable, {
  ERP_REQUEST_HEADERS,
  mapErpRequestRow,
} from '../components/ErpRequestsTable';
import { useAdminErpDemand, useAdminErpRequests } from '../hooks/useAdminErpDemand';

const erpName = (erpSystem) => ERP_SYSTEM_NAMES[erpSystem] ?? 'Other';

/**
 * ERP demand: how many declarant orgs asked for each ERP integration, so the team
 * can pick the next connector (backend docs/features/erp-integration-requests D5).
 * One request per org (D1), so a count is a number of organisations.
 */
export default function ErpDemandPage() {
  const query = useAdminErpDemand();
  const { total = 0, byErp = [] } = query.data ?? {};
  const max = Math.max(1, ...byErp.map((row) => row.count));
  const { tableProps } = useCgTable({
    key: 'admin-erp-requests',
    useData: useAdminErpRequests,
    select: (d) => ({ rows: d?.requests ?? [], total: d?.itemCount ?? 0 }),
    headers: ERP_REQUEST_HEADERS,
    mapRow: mapErpRequestRow,
    defaultLimit: 50,
  });

  return (
    <>
      <AdminPageHeader
        eyebrow="Demand"
        title="ERP integration requests"
        meta={
          query.data
            ? `${total} organisation${total === 1 ? '' : 's'} requested an integration`
            : null
        }
      />
      {query.isPending ? (
        <CgBlockLoader />
      ) : isForbidden(query.error) ? (
        <AccessDeniedPanel />
      ) : query.isError ? (
        <ErrorPanel
          title="Could not load ERP demand"
          onRetry={() => query.refetch()}
        />
      ) : (
        <div
          data-testid="erp-demand-list"
          className="space-y-3 rounded-xl border border-slate-200 bg-white p-5"
        >
          {byErp.map((row) => (
            <RankedBarRow
              key={row.erpSystem}
              leading={
                <>
                  <span className="truncate text-sm font-medium text-slate-900">
                    {erpName(row.erpSystem)}
                  </span>
                  <span className="text-xs text-slate-400">
                    {row.lastRequestedAt
                      ? `Latest ${formatDateTime(row.lastRequestedAt, DATE_FORMAT.MEDIUM)}`
                      : 'No requests'}
                  </span>
                </>
              }
              leadingClassName="flex w-[220px] shrink-0 flex-col"
              barPct={(row.count / max) * 100}
              valueLabel={row.count}
              percentLabel={total ? `${Math.round((row.count / total) * 100)}%` : null}
            />
          ))}
        </div>
      )}
      {query.isSuccess ? (
        <section className="mt-8">
          <h2 className="mb-3 text-base font-semibold text-slate-900">All requests</h2>
          <ErpRequestsTable tableProps={tableProps} />
        </section>
      ) : null}
    </>
  );
}
